import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { PageHeader } from '../../../components/ui/PageHeader'
import { Spinner } from '../../../components/ui/Spinner'
import { Modal } from '../../../components/ui/Modal'
import { Pagination } from '../../../components/ui/Pagination'
import { Alert } from '../../../components/ui/Alert'
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog'
import { FormField, inputClass } from '../../../components/ui/FormField'
import { useClubes, useCreateClube, useUpdateClube, useDeleteClube } from '../hooks/useClubes'
import { clubeFormSchema, type ClubeFormData } from '../schemas'
import type { Clube } from '../types'

export function ClubesPage() {
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState({ nome: '', cidade: '', estado: '' })
  const [search, setSearch] = useState({ nome: '', cidade: '', estado: '' })
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Clube | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Clube | null>(null)
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const { data, isLoading } = useClubes({ ...search, page, limit: 20 })
  const createClube = useCreateClube()
  const updateClube = useUpdateClube()
  const deleteClube = useDeleteClube()

  const emptyForm: ClubeFormData = {
    nome: '',
    razao_social: null,
    nome_fantasia: null,
    cidade: '',
    estado: '',
    telefone: null,
    email: null,
    logradouro: null,
    numero: null,
    bairro: null,
    cep: null,
    inscricao_estadual: null,
    cnpj: null,
    cr_clube: null,
  }

  const form = useForm<ClubeFormData>({
    resolver: zodResolver(clubeFormSchema),
    defaultValues: emptyForm,
  })

  function openCreate() {
    setEditing(null)
    form.reset(emptyForm)
    setModalOpen(true)
  }

  function openEdit(clube: Clube) {
    setEditing(clube)
    form.reset({
      nome: clube.nome,
      razao_social: clube.razao_social ?? '',
      nome_fantasia: clube.nome_fantasia ?? '',
      cidade: clube.cidade,
      estado: clube.estado,
      telefone: clube.telefone ?? '',
      email: clube.email ?? '',
      logradouro: clube.logradouro ?? '',
      numero: clube.numero ?? '',
      bairro: clube.bairro ?? '',
      cep: clube.cep ?? '',
      inscricao_estadual: clube.inscricao_estadual ?? '',
      cnpj: clube.cnpj ?? '',
      cr_clube: clube.cr_clube ?? '',
    })
    setModalOpen(true)
  }

  async function handleSubmit(formData: ClubeFormData) {
    try {
      if (editing) {
        await updateClube.mutateAsync({ id: editing.idclube, data: formData })
        setAlert({ type: 'success', message: 'Clube atualizado com sucesso' })
      } else {
        await createClube.mutateAsync(formData as any)
        setAlert({ type: 'success', message: 'Clube cadastrado com sucesso' })
      }
      setModalOpen(false)
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao salvar clube' })
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    try {
      await deleteClube.mutateAsync(deleteTarget.idclube)
      setAlert({ type: 'success', message: 'Clube excluido com sucesso' })
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao excluir clube' })
    } finally {
      setDeleteTarget(null)
    }
  }

  function handleSearch() {
    setPage(1)
    setSearch({ ...filters })
  }

  function handleClear() {
    setFilters({ nome: '', cidade: '', estado: '' })
    setSearch({ nome: '', cidade: '', estado: '' })
    setPage(1)
  }

  const meta = data?.meta
  const isSaving = createClube.isPending || updateClube.isPending

  return (
    <div className="space-y-4">
      <PageHeader
        title="Clubes"
        description="Gerenciar clubes de tiro"
        action={
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro transition-colors"
          >
            + Novo Clube
          </button>
        }
      />

      {alert && (
        <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
      )}

      {/* Filtros */}
      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Nome</label>
            <input
              className={inputClass}
              placeholder="Filtrar por nome..."
              value={filters.nome}
              onChange={e => setFilters(f => ({ ...f, nome: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Cidade</label>
            <input
              className={inputClass}
              placeholder="Filtrar por cidade..."
              value={filters.cidade}
              onChange={e => setFilters(f => ({ ...f, cidade: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div className="w-28">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Estado</label>
            <input
              className={inputClass}
              placeholder="UF"
              maxLength={2}
              value={filters.estado}
              onChange={e => setFilters(f => ({ ...f, estado: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div className="flex gap-2">
            <button onClick={handleSearch} className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro">
              Buscar
            </button>
            <button onClick={handleClear} className="px-4 py-2 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto">
              Limpar
            </button>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : !data?.data?.length ? (
          <div className="text-center py-16 text-compumaq-textoSuave">
            <p className="text-4xl mb-3">🏢</p>
            <p className="font-medium">Nenhum clube encontrado</p>
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">ID</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Nome / Razao Social</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Cidade</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Estado</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Telefone</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Email</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">CR Clube</th>
                  <th className="text-right px-4 py-3 font-medium text-compumaq-textoSuave">Acoes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-compumaq-dourado/25">
                {data.data.map((clube) => (
                  <tr key={clube.idclube} className="hover:bg-compumaq-preto transition-colors">
                    <td className="px-4 py-3 text-compumaq-textoSuave">{clube.idclube}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-compumaq-texto">{clube.nome}</p>
                      {clube.razao_social && <p className="text-xs text-compumaq-textoSuave">{clube.razao_social}</p>}
                    </td>
                    <td className="px-4 py-3 text-compumaq-textoSuave">{clube.cidade}</td>
                    <td className="px-4 py-3">
                      <span className="inline-block bg-compumaq-dourado/15 text-compumaq-doradoClaro text-xs font-medium px-2 py-0.5 rounded">
                        {clube.estado}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-compumaq-textoSuave">{clube.telefone ?? '—'}</td>
                    <td className="px-4 py-3 text-compumaq-textoSuave">{clube.email ?? '—'}</td>
                    <td className="px-4 py-3 text-compumaq-textoSuave">{clube.cr_clube ?? '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEdit(clube)}
                          className="text-xs text-compumaq-doradoClaro hover:text-compumaq-doradoClaro font-medium"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => setDeleteTarget(clube)}
                          className="text-xs text-red-600 hover:text-red-800 font-medium"
                        >
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {meta && meta.totalPages > 1 && (
              <Pagination
                page={page}
                totalPages={meta.totalPages}
                total={meta.total}
                limit={20}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>

      {/* Modal form */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Editar Clube' : 'Novo Clube'}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">

          {/* Identificacao */}
          <div className="border-b border-compumaq-dourado/25 pb-3">
            <p className="text-xs font-semibold text-compumaq-textoSuave uppercase tracking-wide mb-3">Identificacao</p>
            <div className="space-y-3">
              <FormField label="Nome (sigla/apelido)" required error={form.formState.errors.nome?.message}>
                <input className={inputClass} {...form.register('nome')} placeholder="Ex: CTN, CTSP" />
              </FormField>
              <FormField label="Razao Social" error={form.formState.errors.razao_social?.message}>
                <input className={inputClass} {...form.register('razao_social')} placeholder="Razao social completa" />
              </FormField>
              <FormField label="Nome Fantasia" error={form.formState.errors.nome_fantasia?.message}>
                <input className={inputClass} {...form.register('nome_fantasia')} placeholder="Nome fantasia" />
              </FormField>
            </div>
          </div>

          {/* Documentos */}
          <div className="border-b border-compumaq-dourado/25 pb-3">
            <p className="text-xs font-semibold text-compumaq-textoSuave uppercase tracking-wide mb-3">Documentos</p>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="CNPJ" error={form.formState.errors.cnpj?.message}>
                <input className={inputClass} {...form.register('cnpj')} placeholder="00.000.000/0000-00" maxLength={18} />
              </FormField>
              <FormField label="Inscricao Estadual" error={form.formState.errors.inscricao_estadual?.message}>
                <input className={inputClass} {...form.register('inscricao_estadual')} placeholder="IE" />
              </FormField>
              <div className="col-span-2">
                <FormField label="CR Clube (Certificado de Registro)" error={form.formState.errors.cr_clube?.message}>
                  <input className={inputClass} {...form.register('cr_clube')} placeholder="N. do CR" />
                </FormField>
              </div>
            </div>
          </div>

          {/* Contato */}
          <div className="border-b border-compumaq-dourado/25 pb-3">
            <p className="text-xs font-semibold text-compumaq-textoSuave uppercase tracking-wide mb-3">Contato</p>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Telefone" error={form.formState.errors.telefone?.message}>
                <input className={inputClass} {...form.register('telefone')} placeholder="(11) 99999-9999" />
              </FormField>
              <FormField label="Email" error={form.formState.errors.email?.message}>
                <input className={inputClass} type="email" {...form.register('email')} placeholder="contato@clube.com.br" />
              </FormField>
            </div>
          </div>

          {/* Endereco */}
          <div>
            <p className="text-xs font-semibold text-compumaq-textoSuave uppercase tracking-wide mb-3">Endereco</p>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <FormField label="Logradouro" error={form.formState.errors.logradouro?.message}>
                    <input className={inputClass} {...form.register('logradouro')} placeholder="Rua, Av, etc." />
                  </FormField>
                </div>
                <FormField label="Numero" error={form.formState.errors.numero?.message}>
                  <input className={inputClass} {...form.register('numero')} placeholder="N." />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Bairro" error={form.formState.errors.bairro?.message}>
                  <input className={inputClass} {...form.register('bairro')} placeholder="Bairro" />
                </FormField>
                <FormField label="CEP" error={form.formState.errors.cep?.message}>
                  <input className={inputClass} {...form.register('cep')} placeholder="00000-000" maxLength={9} />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Cidade" required error={form.formState.errors.cidade?.message}>
                  <input className={inputClass} {...form.register('cidade')} placeholder="Cidade" />
                </FormField>
                <FormField label="Estado (UF)" required error={form.formState.errors.estado?.message}>
                  <input className={inputClass} {...form.register('estado')} placeholder="SP" maxLength={2} />
                </FormField>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro disabled:opacity-50"
            >
              {isSaving ? 'Salvando...' : editing ? 'Atualizar' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Excluir Clube"
        message={`Tem certeza que deseja excluir o clube "${deleteTarget?.nome}"?`}
        loading={deleteClube.isPending}
      />
    </div>
  )
}
